/**
 * Wave 2 — the computed columns, from `qf-scr`.
 *
 * Wave 1 is stored columns from `qf-us` and is untouched by any of this. This
 * is the second wave, and it lands in exactly the same place: `applySnapshot`,
 * which merges by field name. `ScreenerView`'s header already describes that
 * contract — *"a screen arrives in waves, and this is where they are folded
 * together"* — so nothing here has a merge rule of its own.
 *
 * ## One socket, not a poll
 *
 * This asked for `GET /scan/columns` every 30 seconds. A poll can only ever be
 * as fresh as its last tick — a value could be a tick old before the request
 * that carried it was even sent — and it paid a full round trip per tick whether
 * or not anything had changed. {@link ScanColumnsClient} holds one socket to
 * `qf-scr` instead: the column set is registered on it, and the compute tier
 * pushes the instant a tick finishes.
 *
 * **First paint does not wait for a tick.** The server pushes on connect from
 * the frame it already holds, which is the property the poll had by accident
 * (its first fetch was immediate) and which a naive socket would lose.
 *
 * ## Two sockets, one page, and neither waits for the other
 *
 * The obvious design is to chain them: take wave 1's resolved trading day and
 * hand it to wave 2, so there is one resolver and one answer. It was rejected
 * because it **serialises** — computed columns could not even be asked for
 * until stored ones had replied, which is latency added to the slower half to
 * prevent a disagreement that is rare.
 *
 * So both tiers normalise `targetDate` independently and each says which day it
 * resolved. They read the same Redis calendar and agree except when one holds a
 * stale cache. Carrying the answer is what makes that visible rather than
 * drawing computed columns from one day beside prices from another.
 *
 * ## It is allowed to be absent
 *
 * `VITE_QV_SCAN_URL` unset ⇒ no socket is ever opened and the grid is wave 1
 * only, which is exactly what shipped before `qf-scr` existed. A server that
 * will not answer is the same state: the columns are missing, not wrong, and
 * nothing on screen claims a number it does not have. That is why a socket error
 * is *not* reported as an error here — only a payload that fails to decode is,
 * because retrying does not fix that one.
 */

import { useEffect, useRef } from "react";

import type {
  ConnectionStatus,
  ScanColumnSet,
  ScreenerComputedRequest,
  ScreenerFrame,
  ScreenerSealedConditionRequest,
} from "@qufolio/transport";
import { ScanColumnsClient } from "@qufolio/transport";

import { idToken } from "../lib/session";
import { currentPass } from "../lib/pass";
import { actingAs, currentUserId } from "../lib/user";

/**
 * The scan tier's base. Empty ⇒ wave 2 is not configured and never runs.
 *
 * **Read directly, not through `httpBase`.** That helper returns the admin
 * backend override when one is set, which exists so a developer can point the
 * *whole app* at a deployed qv-data — and `qf-scr` is a different service on a
 * different host. Routing wave 2 through it would send computed columns to
 * whichever backend wave 1 is using, where the route does not exist.
 */
const SCAN_BASE = (import.meta.env.VITE_QV_SCAN_URL ?? "").replace(/\/$/, "");

/** True when a second wave is available at all. */
export const scanConfigured = SCAN_BASE !== "";

/**
 * The socket route on `qf-scr`, under the `/scan` router that serves the HTTP
 * columns route this replaces. **It has to match the server's path exactly**: a
 * WebSocket to a path FastAPI does not route is refused at the handshake, and
 * the browser reports that as a generic connection failure naming no route.
 */
export const SCAN_WS_PATH = "/scan/ws";

/**
 * The socket URL for an HTTP base.
 *
 * `https:` → `wss:`, `http:` → `ws:`, because a hand-typed `ws://` against an
 * HTTPS origin is refused by the browser as mixed content with an error that
 * names neither the scheme nor the setting. A base that is neither — a relative
 * path through the dev proxy — is left relative on purpose: the browser resolves
 * it against the document, which gets the host *and* the scheme right without
 * this having to guess either.
 */
export function scanSocketUrl(base: string): string {
  if (!base) return "";
  if (base.startsWith("https:")) return `wss:${base.slice(base.indexOf("//"))}${SCAN_WS_PATH}`;
  if (base.startsWith("http:")) return `ws:${base.slice(base.indexOf("//"))}${SCAN_WS_PATH}`;
  return `${base}${SCAN_WS_PATH}`;
}

/**
 * How long a changed column set waits before it is sent.
 *
 * It no longer rations round trips — a changed set is one text frame — but it
 * still rations *compute*: each set replaces what this connection wants, and a
 * newly named column the tier does not hold starts a compute for it. A user
 * dragging through groups, or typing an expression in the editor, should not
 * spend one of those per keystroke.
 *
 * The first set is exempt, exactly as wave 1's is: there is nothing before it to
 * coalesce with, and it is the one that paints. So is a set being *retired* —
 * an empty set is one message whatever the user clicked on the way to it.
 */
export const SCAN_DEBOUNCE_MS = 600;

export interface ScanOptions {
  codes: readonly string[];
  mode: "union" | "intersect";
  computed: readonly ScreenerComputedRequest[];
  /** Stored columns on screen that wave 1 left to wave 2 (`source: compute`). */
  live?: readonly string[];
  /**
   * The ticked sealed conditions wave 1 handed over — those whose `field` its
   * `columns` frame marks `source: compute`, chosen the way `live` is. Wave 1
   * is asked for all of them and answers the rest, so no field is answered by
   * both tiers; the merge reads `row[field]` from whichever did.
   */
  sealedConditions?: readonly ScreenerSealedConditionRequest[];
  /** A JP trade-list screen's kind and frequency (`lib/recs.ts`). */
  recs?: { kind: string; frequency: string };
  /** A value signature of the inputs above — the same key wave 1 uses. */
  key: string;
  /** Bumped to re-send the set in force, e.g. by the refresh button. */
  nonce?: number;
  onFrames: (frames: readonly ScreenerFrame[]) => void;
  /**
   * The socket's state, for the consumer to render.
   *
   * **Not optional plumbing, even though an absent wave 2 is ordinary.** A
   * socket that never opens leaves the values from ten minutes ago on the grid,
   * and nothing else distinguishes those from live ones — `packages/transport`'s
   * rule, stated there: *"the consumer renders it, so a stale screen says
   * 'closed' rather than passing for a live one."* It is a note beside the
   * screen rather than an error over it; see `ScreenerView`.
   */
  onStatus?: (status: ConnectionStatus) => void;
  onError?: (message: string) => void;
}

/**
 * Hold one wave 2 socket open for as long as the caller is mounted.
 *
 * Three effects, for three genuinely different lifetimes: the client lives for
 * the component, the set changes underneath it, and Refresh re-sends the set in
 * force. The shape is `useScreenStream`'s, which is wave 1's socket — the two
 * are the same problem and should not be read as two designs.
 */
const NO_LIVE: readonly string[] = [];
const NO_SEALED: readonly ScreenerSealedConditionRequest[] = [];

export function useScanColumns({
  codes,
  mode,
  computed,
  live = NO_LIVE,
  sealedConditions = NO_SEALED,
  recs,
  key,
  nonce = 0,
  onFrames,
  onStatus,
  onError,
}: ScanOptions): void {
  // Refs read at call time, so a parent re-render does not tear down the socket.
  // Same shape as `useScreenStream`, and for the same reason.
  const listeners = useRef({ onFrames, onStatus, onError });
  listeners.current = { onFrames, onStatus, onError };

  const clientRef = useRef<ScanColumnsClient | null>(null);
  /** Whether a set has been registered yet — the first one is not debounced. */
  const subscribed = useRef(false);

  /**
   * The question the socket was last *told*, and the one being asked now.
   *
   * **This is the poll's `cancelled` flag, rebuilt for a connection that
   * outlives the question.** The fetch this replaced was keyed on the selection
   * and its teardown dropped the answer in flight, so a payload for a superseded
   * question could not reach the consumer. A socket has no such teardown: the
   * set changes on a 600ms debounce and the tier keeps pushing the *previous*
   * one every tick in the meantime — into a view that has already dropped its
   * merge baseline for the new group. That push then becomes the grid: the old
   * roster, with every wave 1 column gone.
   */
  const sentKey = useRef<string | null>(null);
  const liveKey = useRef(key);
  liveKey.current = key;

  useEffect(() => {
    if (!scanConfigured) return;

    const client = new ScanColumnsClient({
      url: scanSocketUrl(SCAN_BASE),
      authToken: idToken,
      pass: currentPass,
      actAs: actingAs,
      listeners: {
        onFrames: (frames) => {
          // A payload for the set this connection is still registered with,
          // while the view has moved on, is the old group's answer — see
          // `sentKey`. Dropped rather than merged: the field names match across
          // groups, so it is a plausible wrong number and not a missing one.
          if (sentKey.current !== liveKey.current) return;
          listeners.current.onFrames(frames);
        },
        onStatus: (status) => listeners.current.onStatus?.(status),
        onError: (error) => {
          // **A socket that will not open is ordinary.** `qf-scr` sleeps outside
          // market hours, and a banner over a working wave 1 screen every time
          // it does would report absence as failure. The client is already
          // retrying; a decode failure is the other kind — the payload itself is
          // wrong, and retrying does not fix that.
          if (error.source === "decode") listeners.current.onError?.(error.message);
        },
      },
    });
    clientRef.current = client;

    return () => {
      clientRef.current = null;
      subscribed.current = false;
      sentKey.current = null;
      client.disconnect();
    };
  }, []);

  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    /** Nothing this connection could usefully be registered with. */
    // Live stored columns count as something to ask for: a screen showing the
    // Impulse columns in session is a wave 2 screen even with nothing computed.
    // So do ticked sealed conditions: this tier answers the ones that read its
    // compute columns while the session is open.
    // And so does a Recs screen: its columns are this tier's whatever else the
    // screen shows.
    const retiring =
      codes.length === 0 ||
      (computed.length === 0 && live.length === 0 && sealedConditions.length === 0 && !recs);
    // **Nothing to ask for opens nothing.** A screen with no computed columns is
    // a wave 1 screen, and a socket held open for it would be one connection and
    // one reconnect loop per idle tab. Once a set *has* been registered the rule
    // inverts — see below.
    if (!subscribed.current && retiring) return;

    // Read once: it is a function because it changes when Firebase reports the
    // restored session, and two reads in one message could straddle that.
    const userId = currentUserId();
    const set: ScanColumnSet = {
      codes,
      mode,
      // A roster with no codes has nothing to compute over, and registering
      // columns against an empty universe would ask the tier for values no row
      // can carry. Sent as empty rather than skipped: the set is replaced
      // wholesale, so *retiring* the columns is the message.
      computed: codes.length > 0 ? computed : [],
      live: codes.length > 0 ? live : [],
      sealedConditions: codes.length > 0 ? sealedConditions : [],
      ...(recs && codes.length > 0 ? { recs } : {}),
      // Membership of a track list is per-caller, so without this an intersect
      // containing one collapses to an empty roster. The same value the HTTP
      // route was sent as a query parameter; the token rides the auth frame.
      ...(userId ? { userId } : {}),
    };

    if (!subscribed.current) {
      subscribed.current = true;
      void client.connect().catch(() => {
        // A refused handshake reports itself through the client's own listeners
        // and its backoff takes it from there. This catch exists only so the
        // rejection is not an unhandled one.
      });
      // Safe on a socket that is still handshaking — `subscribe` records the set
      // and `onopen` sends it — so the first column set does not wait for the
      // connection either.
      sentKey.current = key;
      client.subscribe(set);
      return;
    }

    // **Retiring is not debounced.** There is nothing to coalesce with — an
    // empty set is the same message however many times the selection changes on
    // the way to it — and every one of those 600ms is a tick the tier spends on
    // a screen nobody is looking at, pushed at a view that has already dropped
    // its merge baseline.
    if (retiring) {
      sentKey.current = key;
      client.subscribe(set);
      return;
    }

    const timer = setTimeout(() => {
      sentKey.current = key;
      client.subscribe(set);
    }, SCAN_DEBOUNCE_MS);
    // The cancel *is* the debounce: the next change re-runs this effect, and its
    // teardown drops the subscribe the previous one had queued.
    return () => clearTimeout(timer);
    // Keyed by value — see `key`. `codes`/`mode`/`computed` are read through the
    // closure and are exactly what `key` is a signature of, so an unrelated
    // re-render with fresh array identities does not re-register the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Refresh: re-send the set in force. The protocol carries no cache-dropping
  // flag, so this asks again rather than forcing a recompute — worth knowing
  // before reading a refreshed number as a recomputed one.
  const seenNonce = useRef(nonce);
  useEffect(() => {
    if (nonce === seenNonce.current) return;
    seenNonce.current = nonce;
    clientRef.current?.resubscribe();
  }, [nonce]);
}
